import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import type { OrganizationCreatedAction } from '@Application/Organizations/Organizations/Actions/organizationCreated';
import type { OrganizationDeletedAction } from '@Application/Organizations/Organizations/Actions/organizationDeleted';
import type { OrganizationUpdatedAction } from '@Application/Organizations/Organizations/Actions/organizationUpdated';
import type { OrganizationsFetchedAction } from '@Application/Organizations/Organizations/Actions/organizationsFetched';
import OrganizationsListView from '@UI/Store/Organizations/Views/OrganizationsListView';
import OrganizationView from '@UI/Store/Organizations/Views/OrganizationView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type OrganizationsStore = {
	organizations: OrganizationsListView | null;
};

const initialState: OrganizationsStore = {
	organizations: null,
};

type Actions =
	| OrganizationsFetchedAction
	| OrganizationCreatedAction
	| OrganizationUpdatedAction
	| OrganizationDeletedAction
	| UserRevokedAction;

const whenOrganizationsFetched = (
	store: OrganizationsStore,
	action: OrganizationsFetchedAction,
): OrganizationsStore => ({
	...store,
	organizations: new OrganizationsListView(
		action.payload.map((organization) => new OrganizationView(organization)),
		action.accountId,
	),
});

const whenOrganizationCreated = (
	store: OrganizationsStore,
	action: OrganizationCreatedAction,
): OrganizationsStore => {
	if (!store.organizations || store.organizations.accountId !== action.accountId) {
		return store;
	}

	return {
		...store,
		organizations: store.organizations.addOrganization(new OrganizationView(action.payload)),
	};
};

const whenOrganizationUpdated = (
	store: OrganizationsStore,
	action: OrganizationUpdatedAction,
): OrganizationsStore => {
	if (!store.organizations || store.organizations.accountId !== action.accountId) {
		return store;
	}

	return {
		...store,
		organizations: store.organizations.updateOrganization(new OrganizationView(action.payload)),
	};
};

const whenOrganizationDeleted = (
	store: OrganizationsStore,
	action: OrganizationDeletedAction,
): OrganizationsStore => {
	if (!store.organizations || store.organizations.accountId !== action.accountId) {
		return store;
	}

	return {
		...store,
		organizations: store.organizations.removeOrganization(action.payload.organizationId),
	};
};

const whenUserRevoked = (): OrganizationsStore => initialState;

export default registerStore<'organizationsStore', Actions>(
	'organizationsStore',
	createSliceReducer({
		ORGANIZATIONS_FETCHED: whenOrganizationsFetched,
		ORGANIZATION_CREATED: whenOrganizationCreated,
		ORGANIZATION_UPDATED: whenOrganizationUpdated,
		ORGANIZATION_DELETED: whenOrganizationDeleted,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
